import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { Card, Row, Col, Badge, Button, ProgressBar } from "react-bootstrap";
import {
  BarChart2,
  Users,
  DollarSign,
  CheckCircle,
  CreditCard,
  XCircle,
  ChevronDown,
  ChevronUp,
  Percent,
  User,
  Star,
} from "react-feather";
import JourneyStep from "./components/JourneyStep";
import ValetTipOptions from "./components/ValteTipOptions";
import { FLOW_LABELS, SERVICE_LABELS } from "../../constants";

export default function QRAnalyticsCard({
  hotelName = "Unknown Hotel",
  clientModel = "unknown",
  qrImageUrl,
  locationName,
  serviceType = "unknown",
  flowType = "standard",
  stats = {
    totalSessions: 0,
    enteredTip: 0,
    teamTipOptionSelected: 0,
    directTipOptionSelected: 0,

    successfullyPaid: 0,
    feePaid: 0,
    feeDeclined: 0,
    qrScans: 0,
    poolTips: 0,
    directTips: 0,
    totalTipAmount: 0,
    roomNumberEntered: 0,
    maidNameEntered: 0,
    reviewAdded: 0,
    processingFeeConfirmed: 0,
  },
}) {
  const [showDetails, setShowDetails] = useState(false);
  const [activeTab, setActiveTab] = useState("funnel");
  const isValetFlow = serviceType?.toLowerCase() === "valet";

  const tipConversionRate = stats.totalSessions
    ? Math.round((stats.enteredTip / stats.totalSessions) * 100)
    : 0;
  const paymentConversionRate = stats.enteredTip
    ? Math.round((stats.successfullyPaid / stats.enteredTip) * 100)
    : 0;
  const feePaidRate =
    stats.feePaid + stats.feeDeclined > 0
      ? Math.round((stats.feePaid / (stats.feePaid + stats.feeDeclined)) * 100)
      : 0;
  const overallConversion = stats.totalSessions
    ? Math.round((stats.successfullyPaid / stats.totalSessions) * 100)
    : 0;
  const poolTipPercentage = stats.successfullyPaid
    ? Math.round((stats.poolTips / stats.successfullyPaid) * 100)
    : 0;
  const directTipPercentage = stats.successfullyPaid
    ? Math.round((stats.directTips / stats.successfullyPaid) * 100)
    : 0;
  const averageTipAmount = stats.successfullyPaid
    ? Math.round((stats.totalTipAmount / stats.successfullyPaid) * 100) / 100
    : 0;
  const maidEnteredRate = stats.totalSessions
    ? Math.round((stats.maidNameEntered / stats.totalSessions) * 100)
    : 0;
  const tipSelectedRate = isValetFlow
    ? Math.round((stats.enteredTip / stats.totalSessions) * 100)
    : stats.maidNameEntered
    ? Math.round((stats.enteredTip / stats.maidNameEntered) * 100)
    : 0;
  const reviewRate = stats.enteredTip
    ? Math.round((stats.reviewAdded / stats.enteredTip) * 100)
    : 0;
  const feeConfirmedRate = stats.reviewAdded
    ? Math.round((stats.processingFeeConfirmed / stats.reviewAdded) * 100)
    : 0;
  const paymentRate = stats.processingFeeConfirmed
    ? Math.round((stats.successfullyPaid / stats.processingFeeConfirmed) * 100)
    : 0;

  const FLOW_CONFIG = {
    standard: [
      {
        key: "totalSessions",
        label: "Sessions",
        icon: <Users size={20} />,
      },
      {
        key: "maidNameEntered",
        label: "Name Entered",
        icon: <User size={20} />,
      },
      {
        key: "enteredTip",
        label: "Tip Entered",
        icon: <DollarSign size={20} />,
      },
      {
        key: "reviewAdded",
        label: "Review Added",
        icon: <Star size={20} />,
      },
      {
        key: "processingFeeConfirmed",
        label: "Fee Confirmation",
        icon: <CreditCard size={20} />,
      },
      {
        key: "successfullyPaid",
        label: "Paid",
        icon: <CheckCircle size={20} />,
      },
    ],
    direct: [
      {
        key: "totalSessions",
        label: "Sessions",
        icon: <Users size={20} />,
      },
      {
        key: "enteredTip",
        label: "Tip Entered",
        icon: <DollarSign size={20} />,
      },
      {
        key: "reviewAdded",
        label: "Review Added",
        icon: <Star size={20} />,
      },
      {
        key: "processingFeeConfirmed",
        label: "Fee Confirmation",
        icon: <CreditCard size={20} />,
      },
      {
        key: "successfullyPaid",
        label: "Paid",
        icon: <CheckCircle size={20} />,
      },
    ],
    check_in: [
      {
        key: "totalSessions",
        label: "Sessions",
        icon: <Users size={20} />,
      },
      {
        key: "roomNumberEntered",
        label: "Entered Room Number",
        icon: <User size={20} />,
      },
      {
        key: "enteredTip",
        label: "Tip Entered",
        icon: <DollarSign size={20} />,
      },
      {
        key: "reviewAdded",
        label: "Review Added",
        icon: <Star size={20} />,
      },
      {
        key: "processingFeeConfirmed",
        label: "Fee Confirmation",
        icon: <CreditCard size={20} />,
      },
      {
        key: "successfullyPaid",
        label: "Paid",
        icon: <CheckCircle size={20} />,
      },
    ],
    multiple_check_in: [
      {
        key: "totalSessions",
        label: "Sessions",
        icon: <Users size={20} />,
      },
      {
        key: "roomNumberEntered",
        label: "Entered Room Number",
        icon: <User size={20} />,
      },
      {
        key: "enteredTip",
        label: "Tip Entered",
        icon: <DollarSign size={20} />,
      },
      {
        key: "reviewAdded",
        label: "Review Added",
        icon: <Star size={20} />,
      },
      {
        key: "processingFeeConfirmed",
        label: "Fee Confirmation",
        icon: <CreditCard size={20} />,
      },
      {
        key: "successfullyPaid",
        label: "Paid",
        icon: <CheckCircle size={20} />,
      },
    ],
  };

  const flow = flowType;
  const steps = FLOW_CONFIG[flow] || FLOW_CONFIG.standard;

  const calculateConversion = (idx, steps, stats, flow) => {
    const currentKey = steps[idx].key;
    const prevKey = idx > 0 ? steps[idx - 1].key : null;

    // Skip name/room for valet
    if (
      serviceType?.toLowerCase() === "valet" &&
      currentKey === "maidNameEntered"
    ) {
      return null;
    }

    // Tip Entered conversion for valet uses team+direct tips
    if (serviceType?.toLowerCase() === "valet" && currentKey === "enteredTip") {
      const tipOptionsTotal =
        (stats.teamTipOptionSelected || 0) +
        (stats.directTipOptionSelected || 0);
      return tipOptionsTotal > 0
        ? Math.round((stats.enteredTip / tipOptionsTotal) * 100)
        : 0;
    }

    const currentValue = stats[currentKey] || 0;
    const prevValue = prevKey ? stats[prevKey] || 0 : stats.totalSessions || 1;

    let conversion =
      prevValue > 0 ? Math.round((currentValue / prevValue) * 100) : 0;

    return conversion;
  };

  const getStepColor = (key) => {
    switch (key) {
      case "maidNameEntered":
      case "roomNumberEntered":
        return "secondary";
      case "enteredTip":
        return "success";
      case "reviewAdded":
        return "info";
      case "processingFeeConfirmed":
        return "warning";
      case "successfullyPaid":
        return "primary";
      default:
        return "primary";
    }
  };

  const styles = {
    shadow: {
      boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    },
  };

  return (
    <Card style={styles.shadow} className="rounded-4 border-0">
      <Card.Header className="bg-white">
        <Row className="align-items-center">
          <Col
            xs={12}
            md={6}
            className="d-flex align-items-center mb-3 mb-md-0"
          >
            <div className="p-2 me-3">
              <img
                src={qrImageUrl || "https://via.placeholder.com/50"}
                alt="QR Code"
                style={{ width: "100px", height: "100px" }}
              />
            </div>
            <div>
              <h5 className="mb-0">
                {clientModel.b2b === true || clientModel.isAdminX == true ? (
                  <span
                    className={`border border-1 py-0 px-1 rounded-1 me-1 fw-normal text-success border-success`}
                    style={{ fontSize: "10px" }}
                  >
                    B2B
                  </span>
                ) : clientModel.b2c == true ? (
                  <span
                    className={`border border-1 py-0 px-1 rounded-1 me-1 fw-normal text-primary bg-light border-primary`}
                    style={{ fontSize: "10px" }}
                  >
                    B2C
                  </span>
                ) : null}{" "}
                {hotelName}
              </h5>
              <p className="mb-1 text-muted fs-7">{locationName}</p>

              <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                <small className="text-muted border border-1 py-0 px-1 rounded-1">
                  <span className="fw-semibold">Flow:</span>{" "}
                  {FLOW_LABELS[flowType?.toLowerCase()] ||
                    flowType ||
                    "Unknown"}
                </small>
                <small className="text-muted border border-1 py-0 px-1 rounded-1">
                  <span className="fw-semibold">Service:</span>{" "}
                  {SERVICE_LABELS[serviceType?.toLowerCase()] ||
                    serviceType ||
                    "Unknown"}
                </small>
              </div>
            </div>

            {/* <div>
              <h5 className="mb-0">{hotelName}</h5>
              <p className="mb-0 text-muted fs-7">{"Location Name"}</p>
              <p className="mb-0 text-muted fs-7">{flowType}</p>
              <span
                className={`border border-1 py-0 px-1 rounded-1 me-1 fw-normal text-primary bg-light border-primary`}
                style={{ fontSize: "11px" }}
              >
                {serviceType}
              </span>
            </div> */}
          </Col>
          <Col xs={12} md={6} className="d-flex justify-content-md-end">
            <Button
              variant={showDetails ? "primary" : "outline-primary"}
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              className="d-flex align-items-center border-0"
            >
              {showDetails ? (
                <>
                  <ChevronUp size={16} className="me-1" /> Hide Details
                </>
              ) : (
                <>
                  <ChevronDown size={16} className="me-1" /> Show Details
                </>
              )}
            </Button>
          </Col>
        </Row>
      </Card.Header>

      <Card.Body>
        <Row className="g-3 mb-3">
          <Col xs={6} md={3}>
            <div className=" rounded p-3 h-100">
              <div className="d-flex align-items-center">
                <div className="me-3 p-2 bg-primary bg-opacity-10 rounded-circle">
                  <Users size={18} className="text-primary" />
                </div>
                <div>
                  <div className="small text-muted">Sessions</div>
                  <div className="h4 mb-0">{stats.totalSessions}</div>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className=" rounded p-3 h-100">
              <div className="d-flex align-items-center">
                <div className="me-3 p-2 bg-success bg-opacity-10 rounded-circle">
                  <DollarSign size={18} className="text-success" />
                </div>
                <div>
                  <div className="small text-muted">Tip Entered</div>
                  <div className="h4 mb-0">{stats.enteredTip}</div>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className=" rounded p-3 h-100">
              <div className="d-flex align-items-center">
                <div className="me-3 p-2 bg-info bg-opacity-10 rounded-circle">
                  <CheckCircle size={18} className="text-info" />
                </div>
                <div>
                  <div className="small text-muted">Successfully Paid</div>
                  <div className="h4 mb-0">{stats.successfullyPaid}</div>
                </div>
              </div>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className=" rounded p-3 h-100">
              <div className="d-flex align-items-center">
                <div className="me-3 p-2 bg-dark bg-opacity-10 rounded-circle">
                  <Percent size={18} className="text-dark" />
                </div>
                <div>
                  <div className="small text-muted">Conversion</div>
                  <div className="h4 mb-0">{overallConversion}%</div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {showDetails && (
          <>
            <div className="mb-3 border-bottom">
              <ul className="nav nav-tabs">
                {["funnel", "fees", "additional"].map((tab) => (
                  <li className="nav-item" key={tab}>
                    <button
                      className={`nav-link ${
                        activeTab === tab ? "active" : ""
                      }`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab === "funnel"
                        ? "Conversion Funnel"
                        : tab === "fees"
                        ? "Processing Fee"
                        : "Additional Metrics"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="tab-content">
              {activeTab === "funnel" && (
                <div className="tab-pane active">
                  <h6 className="mb-3">Customer Journey</h6>

                  {/* -------------------------------- Step Config per flow ------------------------------------ */}
                  <>
                    <div className="d-flex justify-content-between text-center mb-3 flex-wrap">
                      {steps.map((step, idx) => {
                        // Case: valet + standard flow → replace maidNameEntered
                        if (
                          flow === "standard" &&
                          serviceType?.toLowerCase() === "valet" &&
                          step.key === "maidNameEntered"
                        ) {
                          return (
                            <ValetTipOptions
                              key="valet-tip-options"
                              stats={stats}
                            />
                          );
                        }

                        return (
                          <JourneyStep
                            key={step.key}
                            icon={step.icon}
                            value={stats[step.key] || 0}
                            label={step.label}
                            conversion={
                              idx === 0
                                ? null
                                : calculateConversion(
                                    idx,
                                    steps,
                                    stats,
                                    flow,
                                    serviceType
                                  )
                            }
                            bg={`bg-${getStepColor(step.key)}`}
                          />
                        );
                      })}
                    </div>
                    {/* <div className="d-flex justify-content-between text-center mb-3 flex-wrap">
                      {steps.map((step, idx) => (
                        <JourneyStep
                          key={step.key}
                          icon={step.icon}
                          value={stats[step.key] || 0}
                          label={step.label}
                          conversion={
                            idx === 0
                              ? null
                              : calculateConversion(idx, steps, stats, flow)
                          }
                          bg={`bg-${getStepColor(step.key)}`}
                        />
                      ))}
                    </div> */}

                    <ProgressBar className="mb-2">
                      {steps.map((step, idx) => (
                        <ProgressBar
                          key={step.key}
                          variant={getStepColor(step.key)}
                          now={calculateConversion(idx, steps, stats, flow)}
                        />
                      ))}
                    </ProgressBar>

                    {/* <div className="d-flex justify-content-between small text-muted flex-wrap">
                      {steps.map((step, idx) => (
                        <span key={step.key}>
                          {step.label} (
                          {calculateConversion(idx, steps, stats, flow)}
                          %)
                        </span>
                      ))}
                    </div> */}
                  </>
                </div>
                // <div className="tab-pane active">
                //   <h6 className="mb-3">Customer Journey</h6>
                //   <div className="d-flex justify-content-between text-center mb-3 flex-wrap">
                //     {/* Sessions */}
                //     <div>
                //       <div
                //         className="bg-primary text-white rounded-circle d-inline-flex align-items-center justify-content-center mb-2"
                //         style={{ width: 50, height: 50 }}
                //       >
                //         <Users size={20} />
                //       </div>
                //       <div className="h5 mb-0">{stats.totalSessions}</div>
                //       <div className="small text-muted">Sessions</div>
                //     </div>
                //     {isValetFlow ? (
                //       <>
                //         <div className="d-flex flex-column justify-content-center">
                //           <FontAwesomeIcon icon={faArrowRight} />
                //         </div>
                //         <div className="d-flex flex-column justify-content-center align-items-center">
                //           <div className="d-flex gap-4">
                //             <div className="d-flex flex-column align-items-center">
                //               <div
                //                 className="rounded-circle bg-light text-dark d-flex align-items-center justify-content-center mb-1"
                //                 style={{
                //                   width: "32px",
                //                   height: "32px",
                //                   fontSize: "0.9rem",
                //                 }}
                //               >
                //                 {stats.teamTipOptionSelected}
                //               </div>
                //               <div className="small text-muted">Team Tips</div>
                //             </div>

                //             <div className="d-flex flex-column align-items-center">
                //               <div
                //                 className="rounded-circle bg-light text-dark d-flex align-items-center justify-content-center mb-1"
                //                 style={{
                //                   width: "32px",
                //                   height: "32px",
                //                   fontSize: "0.9rem",
                //                 }}
                //               >
                //                 {stats.directTipOptionSelected}
                //               </div>
                //               <div className="small text-muted">
                //                 Direct Tips
                //               </div>
                //             </div>
                //           </div>
                //         </div>
                //       </>
                //     ) : (
                //       <>
                //         <JourneyStep
                //           icon={<User size={20} />}
                //           value={stats.maidNameEntered}
                //           label="Name Entered"
                //           conversion={maidEnteredRate}
                //         />
                //       </>
                //     )}

                //     <JourneyStep
                //       icon={<DollarSign size={20} />}
                //       value={stats.enteredTip}
                //       label="Tip Entered"
                //       conversion={tipSelectedRate}
                //       bg="bg-success"
                //     />

                //     <JourneyStep
                //       icon={<Star size={20} />}
                //       value={stats.reviewAdded}
                //       label="Review Added"
                //       conversion={reviewRate}
                //       bg="bg-info"
                //     />

                //     <JourneyStep
                //       icon={<CreditCard size={20} />}
                //       value={stats.processingFeeConfirmed}
                //       label="Fee Confirmation"
                //       conversion={feeConfirmedRate}
                //       bg="bg-warning"
                //     />
                //     <JourneyStep
                //       icon={<CheckCircle size={20} />}
                //       value={stats.successfullyPaid}
                //       label="Paid"
                //       conversion={paymentRate}
                //       bg="bg-primary"
                //     />
                //   </div>

                //   <ProgressBar className="mb-2">
                //     <ProgressBar variant="primary" now={100} />
                //     {!isValetFlow && (
                //       <ProgressBar variant="secondary" now={maidEnteredRate} />
                //     )}
                //     <ProgressBar variant="success" now={tipSelectedRate} />
                //     <ProgressBar variant="info" now={reviewRate} />
                //     <ProgressBar variant="warning" now={feeConfirmedRate} />
                //     <ProgressBar variant="primary" now={paymentRate} />
                //   </ProgressBar>

                //   <div className="d-flex justify-content-between small text-muted flex-wrap">
                //     <span>Start</span>
                //     {!isValetFlow && (
                //       <span>Name Entered ({maidEnteredRate}%)</span>
                //     )}
                //     <span>Tip Entered ({tipSelectedRate}%)</span>
                //     <span>Review Addedd ({reviewRate}%)</span>
                //     <span>Fee Confirmation ({feeConfirmedRate}%)</span>
                //     <span>Completed ({paymentRate}%)</span>
                //   </div>
                // </div>
              )}

              {activeTab === "fees" && (
                <div className="tab-pane active">
                  <Row>
                    <Col md={7}>
                      <h6 className="mb-3">Processing Fee Paid by</h6>
                      <ProgressBar className="mb-2" style={{ height: "30px" }}>
                        <ProgressBar
                          variant="success"
                          now={feePaidRate}
                          label={`Paid: ${feePaidRate}%`}
                        />
                        <ProgressBar
                          variant="danger"
                          now={100 - feePaidRate}
                          label={`Declined: ${100 - feePaidRate}%`}
                        />
                      </ProgressBar>
                      <div className="d-flex justify-content-between mt-3">
                        <div className="text-center">
                          <CreditCard size={18} className="text-success me-1" />
                          <div className="h4">{stats.feePaid}</div>
                          <small className="text-muted">
                            Fee Paid by Customer
                          </small>
                        </div>
                        <div className="text-center">
                          <XCircle size={18} className="text-danger me-1" />
                          <div className="h4">{stats.feeDeclined}</div>
                          <small className="text-muted">
                            Fee Paid by Staff{" "}
                          </small>
                        </div>
                      </div>
                    </Col>
                    <Col md={5}>
                      <h6 className="mb-3">Fee Impact</h6>
                      <Card className="bg-light">
                        <Card.Body className="text-center">
                          <div className="display-5 fw-bold">
                            {feePaidRate}%
                          </div>
                          <div className="small text-muted mb-3">
                            Paid by Customer Rate
                          </div>
                          {/* <div className="h5 mb-0">
                            +${(stats.feePaid * 0.5).toFixed(2)}
                          </div>
                          <div className="small text-muted">
                            assuming $0.50 fee
                          </div> */}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}

              {activeTab === "additional" && (
                <div className="tab-pane active">
                  <Row>
                    <Col md={6}>
                      <Card className="border h-100">
                        <Card.Body className="text-center">
                          <BarChart2 size={20} className="text-primary mb-2" />
                          <h6>QR Code Performance</h6>
                          <div className="display-5 fw-bold">
                            {stats.qrScans
                              ? Math.round(
                                  (stats.successfullyPaid /
                                    stats.totalSessions) *
                                    100
                                )
                              : 0}
                            %
                          </div>
                          <div className="text-muted">Scan-to-Pay Rate</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="border h-100">
                        <Card.Body className="text-center">
                          <BarChart2 size={20} className="text-primary mb-2" />
                          <h6>Conversion Summary</h6>
                          <div className="mb-2">
                            <small className="text-muted">
                              Scan to Tip Entered
                            </small>
                            <div>{tipConversionRate}%</div>
                          </div>
                          <div className="mb-2">
                            <small className="text-muted">
                              Tip Entered to Payment
                            </small>
                            <div>{paymentConversionRate}%</div>
                          </div>
                          <div>
                            <small className="text-muted">
                              Overall Conversion
                            </small>
                            <div>{overallConversion}%</div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={12}>
                      <Card className="border">
                        <Card.Body>
                          <div className="d-flex align-items-center mb-3">
                            <DollarSign
                              size={18}
                              className="text-primary me-2"
                            />
                            <h6 className="mb-0">Tip Types</h6>
                          </div>

                          <Row>
                            <Col md={7}>
                              <h6 className="mb-3 text-center">
                                Pool vs Individual Tips
                              </h6>
                              <ProgressBar
                                className="mb-2"
                                style={{ height: "30px" }}
                              >
                                <ProgressBar
                                  variant="primary"
                                  now={poolTipPercentage}
                                  key={1}
                                  label={`Pool: ${poolTipPercentage}%`}
                                />
                                <ProgressBar
                                  variant="warning"
                                  now={directTipPercentage}
                                  key={2}
                                  label={`Direct: ${directTipPercentage}%`}
                                />
                              </ProgressBar>
                              <div className="d-flex justify-content-between mt-3">
                                <div className="text-center">
                                  <div className="d-flex align-items-center justify-content-center mb-2">
                                    <Users
                                      size={18}
                                      className="text-primary me-1"
                                    />
                                    <span>Pool Tips</span>
                                  </div>
                                  <div className="h4 mb-0">
                                    {stats.poolTips}
                                  </div>
                                  <div className="small text-muted">
                                    ({poolTipPercentage}% of tips)
                                  </div>
                                </div>
                                <div className="text-center">
                                  <div className="d-flex align-items-center justify-content-center mb-2">
                                    <User
                                      size={18}
                                      className="text-warning me-1"
                                    />
                                    <span>Individual Tips</span>
                                  </div>
                                  <div className="h4 mb-0">
                                    {stats.directTips}
                                  </div>
                                  <div className="small text-muted">
                                    ({directTipPercentage}% of tips)
                                  </div>
                                </div>
                              </div>
                            </Col>

                            <Col md={5}>
                              <div className="text-center h-100 d-flex flex-column justify-content-center">
                                <div className="mb-3">
                                  <div className="small text-muted mb-1">
                                    Total Tips
                                  </div>
                                  <div className="h3 mb-0">
                                    {stats.successfullyPaid}
                                  </div>
                                </div>
                                {/* <div className="mb-3">
                                  <div className="small text-muted mb-1">
                                    Average Tip Amount
                                  </div>
                                  <div className="h5 mb-0">
                                    ${averageTipAmount}
                                  </div>
                                </div> */}
                                <div>
                                  <div className="small text-muted mb-1">
                                    Most Common Tip Type
                                  </div>
                                  <div className="h5 mb-0">
                                    {stats.poolTips > stats.directTips ? (
                                      <span className="text-primary">
                                        Pool Tips
                                      </span>
                                    ) : (
                                      <span className="text-warning">
                                        Direct Tips
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
